import { useEffect, useState } from 'react';

import { getCdnBaseUrl } from '@/lib/cdn/config';
import {
  buildCloudflareStrapFallbackCrop,
  buildCloudflareStrapModalCrop,
  clampCropRectangleToBounds,
  type CropRectangle,
  inferSourceDimensionsFromUrl,
  normalizeImageDimensions,
  normalizeSaleorCropCoordinates,
  type SaleorCropCoordinates,
  scaleCropRectangle,
  shouldScaleCropRectangle,
  toCloudflareTrimOptions,
  toCropRectangle,
} from '@/lib/cdn/cropTransform';

interface ResponsiveCropArgs {
  crop: CropRectangle;
  outputHeight: number;
  outputWidth: number;
}

const removeTrailingSlash = (value: string): string =>
  value.replace(/\/+$/, '');

/**
 * Build cropped URL for Cloudflare using trim parameters
 * @see https://developers.cloudflare.com/images/transform-images/transform-via-url/
 */
const buildCloudflareCroppedUrl = (
  crop: CropRectangle,
  src: string,
  baseUrl: string,
): string => {
  const options = [...toCloudflareTrimOptions(crop), 'format=auto'].join(',');

  const proxyPath = `api/image-proxy/${src.replace('https://', '')}`;
  const normalizedBaseUrl = removeTrailingSlash(baseUrl);
  return `${normalizedBaseUrl}/cdn-cgi/image/${options}/${proxyPath}`;
};

/**
 * Build responsive URL for Cloudflare with crop and resize
 */
const buildCloudflareResponsiveUrl = (
  args: ResponsiveCropArgs,
  src: string,
  baseUrl: string,
): string => {
  const options = [
    ...toCloudflareTrimOptions(args.crop),
    `width=${args.outputWidth}`,
    `height=${args.outputHeight}`,
    'fit=cover',
    'format=auto',
  ].join(',');

  const proxyPath = `api/image-proxy/${src.replace('https://', '')}`;
  const normalizedBaseUrl = removeTrailingSlash(baseUrl);
  return `${normalizedBaseUrl}/cdn-cgi/image/${options}/${proxyPath}`;
};

export const useCropSrcset = (
  widths: number[],
  src: string,
  imageDimensions: { height: number; width: number },
  sourceImageDimensions: { height: number; width: number },
  coords: SaleorCropCoordinates,
  strapCropPreset: 'none' | 'circle' | 'modal' = 'none',
) => {
  const [newSrc, setNewSrc] = useState('');
  const [srcSet, setSrcSet] = useState('');

  useEffect(() => {
    if (!src || !src.startsWith('https://')) {
      setNewSrc(src);
      setSrcSet('');
      return;
    }

    const normalizedCoords = normalizeSaleorCropCoordinates(coords);
    let crop = toCropRectangle(normalizedCoords);

    const referenceDimensions = normalizeImageDimensions(imageDimensions);
    const explicitSourceDimensions = normalizeImageDimensions(
      sourceImageDimensions,
    );
    const inferredSourceDimensions = inferSourceDimensionsFromUrl(
      src,
      referenceDimensions,
    );
    const sourceDimensions =
      explicitSourceDimensions ?? inferredSourceDimensions;
    const strapPresetDimensions = sourceDimensions ?? referenceDimensions;
    if (strapCropPreset !== 'none' && strapPresetDimensions) {
      crop =
        strapCropPreset === 'circle'
          ? buildCloudflareStrapFallbackCrop(strapPresetDimensions)
          : buildCloudflareStrapModalCrop(strapPresetDimensions);
    } else if (sourceDimensions) {
      if (
        referenceDimensions &&
        shouldScaleCropRectangle(crop, referenceDimensions, sourceDimensions)
      ) {
        crop = scaleCropRectangle(crop, referenceDimensions, sourceDimensions);
      }
      crop = clampCropRectangleToBounds(crop, sourceDimensions);
    }

    if (crop.width <= 0 || crop.height <= 0) {
      setNewSrc(src);
      setSrcSet('');
      return;
    }

    const baseUrl = getCdnBaseUrl('cloudflare');
    const croppedSrc = buildCloudflareCroppedUrl(crop, src, baseUrl);
    setNewSrc(croppedSrc);

    const generatedSrcSet = widths
      .map((setWidth) => {
        const aspectRatio = crop.height / crop.width;
        const scaledHeight = Math.round(setWidth * aspectRatio);

        const responsiveArgs: ResponsiveCropArgs = {
          crop: crop,
          outputHeight: Math.min(scaledHeight, crop.height),
          outputWidth: Math.min(setWidth, crop.width),
        };

        const url = buildCloudflareResponsiveUrl(responsiveArgs, src, baseUrl);
        return `${url} ${setWidth}w`;
      })
      .join(', ');

    setSrcSet(generatedSrcSet);
  }, [
    widths,
    src,
    imageDimensions,
    sourceImageDimensions,
    coords,
    strapCropPreset,
  ]);

  return { newSrc, srcSet };
};
